/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AppState,
  UserPreference,
  UserPreferences,
  CloningOperation,
  GraphicMode,
  SavedGraphicMode,
  DisplaySettings,
} from 'types';
import { CTFModuleTypes, DocumentTypes } from 'types/inspection';
import initialAppState from './appState';
import { GVSnackMessage } from '../../components/lib/GVSnackbar/GVSnackbar';

const app = createSlice({
  name: 'app',
  initialState: initialAppState,
  reducers: {
    resetStore: (state: AppState, action: PayloadAction<boolean | undefined>) => {
      if (action && action.payload) {
        return initialAppState;
      }
      return {
        ...initialAppState,
        displayName: state.displayName ? state.displayName : initialAppState.displayName,
        userPreferences: state.userPreferences ? state.userPreferences : initialAppState.userPreferences,
        savedDisplaySettings: state.savedDisplaySettings ?? initialAppState.savedDisplaySettings,
      };
    },
    setSnackMessage: (state: AppState, action: PayloadAction<GVSnackMessage>) => {
      // if in initial state add to snackMessage, else add to queue
      if (!state.snackMessage.open) {
        action.payload.open = true;
        state.snackMessage = action.payload;
      } else if (
        !state.snackMessageQueue.some((snackMsg) => snackMsg.message === action.payload.message) &&
        state.snackMessage.message !== action.payload.message
      ) {
        // only add to queue if duplicate message not already in queue
        state.snackMessageQueue = [...state.snackMessageQueue, action.payload];
      }
    },
    clearSnackMessageType: (state: AppState, action: PayloadAction<string>) => {
      state.snackMessage.open = false;
      state.snackMessageQueue = state.snackMessageQueue.filter((snack) => snack.type !== action.payload);
    },
    getNextSnackMessage: (state: AppState) => {
      const [nextMessage, ...restOfQueue] = state.snackMessageQueue;
      if (nextMessage) {
        state.snackMessage = nextMessage;
        state.snackMessageQueue = restOfQueue;
      } else {
        state.snackMessage.open = false;
      }
    },
    setAutosaving: (state: AppState, action: PayloadAction<boolean>) => {
      state.autosaving = action.payload;
    },
    setUpdatingInspection: (state: AppState, action: PayloadAction<boolean>) => {
      state.updatingInspection = action.payload;
    },
    setStartingInspection: (state: AppState, action: PayloadAction<boolean>) => {
      state.startingInspection = action.payload;
    },
    setResetIdleTimer: (state: AppState, action: PayloadAction<boolean>) => {
      state.resetIdleTimer = action.payload;
    },
    traceLoginAttempts: (state: AppState, action: PayloadAction<{ username: string }>) => {
      state.loginAttempts = [...state.loginAttempts, action.payload.username];
    },
    addDialogPopup: (state: AppState, action: PayloadAction<string>) => {
      const notificationToShow = state.popUpNotifications.find((notification) => notification.type === action.payload);
      if (!notificationToShow) {
        state.popUpNotifications = state.popUpNotifications.concat([{ type: action.payload, dismissed: false }]);
      }
    },
    dismissDialogPopup: (state: AppState, action: PayloadAction<string>) => {
      const notificationToDismiss = state.popUpNotifications.find(
        (notification) => notification.type === action.payload,
      );
      if (notificationToDismiss) {
        notificationToDismiss.dismissed = true;
      }
    },
    clearDialogsFromState: (state: AppState) => {
      state.popUpNotifications = [];
    },
    setSavedDisplaySettings: (state: AppState, action: PayloadAction<Partial<DisplaySettings>>) => {
      state.savedDisplaySettings = action.payload;
    },
    setUserPreferences: (state: AppState, action: PayloadAction<UserPreferences>) => {
      state.userPreferences = action.payload;
    },
    updateUserPreferences: (state: AppState, action: PayloadAction<UserPreference>) => {
      (state.userPreferences[action.payload.userPreferenceName] as any) = action.payload.value;
    },
    panToolToastHasShown: (state: AppState) => {
      state.toastStatuses.hasPanToolToastShown = true;
    },
    setIntegrations: (state: AppState, action: PayloadAction<any>) => {
      state.integrations = action.payload;
    },
    setDisplayName: (state: AppState, action: PayloadAction<string>) => {
      state.displayName = action.payload;
    },
    setShowLiveText: (state: AppState, action: PayloadAction<boolean>) => {
      state.showLiveText = action.payload;
    },
    setShowTextHighlights: (state: AppState, action: PayloadAction<boolean>) => {
      state.showTextHighlights = action.payload;
    },
    setFocusedMatchIndex: (state: AppState, action: PayloadAction<number>) => {
      state.focusedMatchIndex = action.payload;
    },
    setShowDifferenceBoxes: (state: AppState, action: PayloadAction<boolean>) => {
      state.showDifferenceBoxes = action.payload;
    },
    setHideOtherDifferences: (state: AppState, action: PayloadAction<boolean>) => {
      state.hideOtherDifferences = action.payload;
    },
    setShowOverlay: (state: AppState, action: PayloadAction<boolean>) => {
      state.showOverlay = action.payload;
    },
    setGrayscaleMode: (state: AppState, action: PayloadAction<boolean>) => {
      state.grayscaleMode = action.payload;
    },
    setSinglePageView: (state: AppState, action: PayloadAction<boolean>) => {
      state.singlePageView = action.payload;
    },
    setShowRepeats: (state: AppState, action: PayloadAction<boolean>) => {
      state.showRepeats = action.payload;
    },
    setGraphicMode: (state: AppState, action: PayloadAction<GraphicMode>) => {
      state.graphicMode = action.payload;
    },
    setLastGraphicMode: (state: AppState, action: PayloadAction<SavedGraphicMode>) => {
      state.lastGraphicMode = action.payload;
    },
    setTransparencyOpacity: (state: AppState, action: PayloadAction<number>) => {
      state.transparencyOpacity = action.payload;
    },
    startCloningOperation: (state: AppState, action: PayloadAction<CloningOperation>) => {
      state.cloningOperations.push(action.payload);
    },
    finishCloningOperation: (state: AppState, action: PayloadAction<string>) => {
      state.cloningOperations = state.cloningOperations.filter((op) => op.id !== action.payload);
    },
    clearAllCloningOperations: (state: AppState) => {
      state.cloningOperations = [];
    },
    setCloningModalOpen: (state: AppState, action: PayloadAction<boolean>) => {
      state.isCloningModalOpen = action.payload;
    },
    setCloseConfirm: (state: AppState, action: PayloadAction<boolean>) => {
      state.closeConfirm = action.payload;
    },
    setIsReopenedSession: (state: AppState, action: PayloadAction<boolean>) => {
      state.isReopenedSession = action.payload;
    },
    addUnavailableFile: (state: AppState, action: PayloadAction<string>) => {
      if (!state.unavailableFiles.includes(action.payload)) {
        state.unavailableFiles.push(action.payload);
      }
    },
    setSelectedModule: (state: AppState, action: PayloadAction<CTFModuleTypes | 'All'>) => {
      state.selectedModule = action.payload;
    },
    setHideDisplayPanels: (state: AppState, action: PayloadAction<{ [key in DocumentTypes]: boolean }>) => {
      state.hideDisplayPanels = action.payload;
    },
    setShowMarkupTab: (state: AppState, action: PayloadAction<boolean>) => {
      state.showMarkupTab = action.payload;
    },
  },
});

export default app;
/* eslint-enable no-param-reassign */
